import { useEffect, useState } from 'react';
import { Box, TableContainer } from '@mui/material';
import { MaterialReactTable, MRT_ColumnDef, MRT_TableOptions, useMaterialReactTable } from 'material-react-table';

import { GettingFileResolutionDto } from 'src/generated/services/TFinancialApi';
import { newFormatDateString } from 'src/utils/date';
import { beautifyEnum, capitalizeName, getStatusLabel } from 'src/utils/utility';
import IconDocument from 'src/assets/images/icons/document.svg';
import { TableMain } from '../actionDetails/noRateCard/TableChargesShipment';
import { TableWrapper } from '../analytics/table/TableDetail';
import { IInternalTableIssuesProps, ROW_ACTION_TYPE, SORTING_DIRECTION } from './types';
import { ButtonRowAction } from './ButtonRowAction';
import { RowActionMenus } from './RowActionMenus';
import StatusChip from '../dashboard/StatusChip';
import { ResolutionStatusChip } from './ResolutionStatusChip';
import { simplifyFileNames } from '../../molecules/uploadInvoice/tools';

// eslint-disable-next-line react-refresh/only-export-components
export const useInternalTableIssues = (tableOptions: MRT_TableOptions<GettingFileResolutionDto>) =>
  useMaterialReactTable({
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableRowSelection: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableSortingRemoval: false,
    enableEditing: true,
    editDisplayMode: 'table',
    muiTableHeadCellProps: {
      align: 'center',
    },
    muiTableBodyCellProps: {
      align: 'center',
    },
    ...tableOptions,
  });

// eslint-disable-next-line react-refresh/only-export-components
export const useInternalColumnsIssue = (): MRT_ColumnDef<GettingFileResolutionDto>[] => {
  const columnList = [
    {
      enableEditing: false,
      enableSorting: true,
      accessorFn: (row: GettingFileResolutionDto) => `${row?.resolutionType}-${row?.id}`,
      accessorKey: 'resolutionCode',
      header: 'Issue ID',
      size: 100,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'resolutionCreatedOn',
      accessorFn: (row: GettingFileResolutionDto) => <>{newFormatDateString(row?.resolutionCreatedOn)}</>,
      header: 'Created On',
      size: 300,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'resolutionUpdatedOn',
      accessorFn: (row: GettingFileResolutionDto) => <>{newFormatDateString(row?.resolutionUpdatedOn)}</>,
      header: 'Last Update On',
      size: 100,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'resolutionStatus',
      accessorFn: (row: GettingFileResolutionDto) => <ResolutionStatusChip status={row?.resolutionStatus} />,
      header: 'Resolution Status',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: false,
      accessorFn: undefined,
      accessorKey: 'invoiceId',
      header: 'Invoice ID',
      size: 300,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'invoiceStatus',
      accessorFn: (row: GettingFileResolutionDto) => (row?.invoiceStatus ? <StatusChip status={row?.invoiceStatus} /> : null),
      header: 'Invoice Status',
      size: 100,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorFn: (row: GettingFileResolutionDto) => (
        <>
          {capitalizeName(row?.reporterFirstName || '')}
          {row?.reporterLastName ? ` ${capitalizeName(row?.reporterLastName)}` : ''}
        </>
      ),
      accessorKey: 'reporterFirstName',
      header: 'Reporter Name',
      size: 160,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorFn: (row: GettingFileResolutionDto) => (
        <>
          {capitalizeName(row?.assigneeFirstName || '')}
          {row?.assigneeLastName ? ` ${capitalizeName(row?.assigneeLastName)}` : ''}
        </>
      ),
      accessorKey: 'assigneeFirstName',
      header: 'Assignee',
      size: 160,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorFn: (row: GettingFileResolutionDto) => getStatusLabel(row?.invoiceFileProcessingStatus),
      accessorKey: 'invoiceFileProcessingStatus',
      header: 'Pipeline Status',
      size: 300,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorFn: (row: GettingFileResolutionDto) => beautifyEnum(row?.statusDetails),
      accessorKey: 'statusDetails',
      header: 'Status Details',
      size: 300,
    },
    {
      enableEditing: false,
      enableSorting: false,
      accessorFn: undefined,
      accessorKey: 'invoiceFileProcessingId',
      header: 'Processing ID',
      size: 300,
    },
    {
      enableEditing: false,
      enableSorting: false,
      accessorFn: undefined,
      accessorKey: 'batchId',
      header: 'Batch ID',
      size: 300,
    },
    {
      enableEditing: false,
      enableSorting: false,
      accessorKey: 'errorMessage',
      accessorFn: (row: GettingFileResolutionDto) => <ButtonRowAction data={row} actionType={ROW_ACTION_TYPE.ERROR_MESSAGE} />,
      header: 'Error Message',
      size: 200,
    },
    {
      enableEditing: false,
      enableSorting: false,
      accessorKey: 'userMessage',
      accessorFn: (row: GettingFileResolutionDto) => <ButtonRowAction data={row} actionType={ROW_ACTION_TYPE.USER_MESSAGE} />,
      header: 'User Error Message',
      size: 200,
    },
    {
      enableEditing: false,
      enableSorting: false,
      accessorKey: 'resolutionMessage',
      accessorFn: (row: GettingFileResolutionDto) => <ButtonRowAction data={row} actionType={ROW_ACTION_TYPE.RESOLUTION_MESSAGE} />,
      header: 'Resolution Message',
      size: 200,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'fileName',
      accessorFn: (row: GettingFileResolutionDto) => (
        <Box display='flex' alignItems='center' justifyContent='center' gap='8px'>
          <img src={IconDocument} alt='Document Icon' width={20} height={20} />
          <span>{simplifyFileNames(row?.fileName)}</span>
        </Box>
      ),
      header: 'File Name',
      size: 100,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'extractedPage',
      accessorFn: undefined,
      header: 'Extracted Pages',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'modelUsed',
      accessorFn: undefined,
      header: 'Model Used',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'transformerUsed',
      accessorFn: undefined,
      header: 'Transformer Used',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'invoiceNumber',
      accessorFn: undefined,
      header: 'Invoice No.',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'serviceProviderName',
      accessorFn: (row: GettingFileResolutionDto) => beautifyEnum(row?.serviceProviderName),
      header: 'Service Provider',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'modality',
      accessorFn: undefined,
      header: 'Modality',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'billedEntity',
      accessorFn: undefined,
      header: 'Billed Entity',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: false,
      accessorKey: 'actions',
      accessorFn: (row: GettingFileResolutionDto) => <RowActionMenus data={row} />,
      header: 'Actions',
      size: 20,
    },
  ];

  return columnList;
};

// Common styles for the last cell in both header and body rows
const lastCellStyles = {
  boxShadow: '-4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid #E5E7EB',
};

export const InternalTableIssues = (props: IInternalTableIssuesProps) => {
  const [sorting, setSorting] = useState([{ id: 'resolutionCreatedOn', desc: true }]);

  const { data, onSorting, isLoading } = props;
  const columns = useInternalColumnsIssue();

  useEffect(() => {
    const runSorting = (sortingConfig) => {
      if (sortingConfig.length > 0) {
        const config = sortingConfig[0];
        const method = config?.desc ? SORTING_DIRECTION.DESC : SORTING_DIRECTION.ASC;
        onSorting(method, config?.id);
      } else {
        onSorting(SORTING_DIRECTION.DESC, 'resolutionCreatedOn');
      }
    };
    runSorting(sorting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  const table = useInternalTableIssues({
    columns,
    data,
    getRowId: (row: GettingFileResolutionDto) => `${row.resolutionType}-${row?.id}`,
    enableSorting: true,
    manualSorting: true,
    initialState: {
      columnPinning: { right: ['actions'] },
    },
    state: {
      sorting,
      isLoading,
    },
    onSortingChange: setSorting,
    renderEmptyRowsFallback: () => <p className='text-center text-16 font-medium uppercase text-neutral-40 py-30'>NO DATA AVAILABLE</p>,
    muiTableHeadProps: {
      sx: {
        // stripe the rows, make odd rows a darker color
        '& th': {
          backgroundColor: '#F5F6FA',
        },
        '& th:last-child': lastCellStyles,
      },
    },
    muiTableBodyProps: {
      sx: {
        // stripe the rows, make odd rows a darker color
        '& td:last-child': lastCellStyles,
        '& td:first-child': {
          boxShadow: 'none',
        },
      },
    },
  });

  return (
    <div className='w-full'>
      <TableContainer className='rounded-b-2xl' component={TableWrapper}>
        <TableMain>
          <MaterialReactTable table={table} />
        </TableMain>
      </TableContainer>
    </div>
  );
};
